// Keep date format strict for API payload compatibility.
const ISO_DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;
const DATE_TIME_LOCAL_REGEX = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/;
export const DEFAULT_GIG_TIME_ZONE = "America/New_York";

export type ZonedDateParts = {
  year: number;
  month: number;
  day: number;
};

export type ZonedDateTimeParts = ZonedDateParts & {
  hour: number;
  minute: number;
  second: number;
  millisecond: number;
};

const parseDateValue = (value: string): Date | null => {
  if (ISO_DATE_REGEX.test(value)) {
    const [yearPart, monthPart, dayPart] = value.split("-");
    return new Date(Number(yearPart), Number(monthPart) - 1, Number(dayPart));
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }

  return parsed;
};

const formatDateValue = (
  value: string | null,
  options: Intl.DateTimeFormatOptions,
  fallback: string,
  timeZone?: string | null,
): string => {
  if (!value) {
    return fallback;
  }

  const parsed = parseDateValue(value);
  if (!parsed) {
    return value;
  }

  const resolvedTimeZone = normalizeTimeZone(timeZone);
  return new Intl.DateTimeFormat(undefined, {
    ...options,
    ...(resolvedTimeZone ? { timeZone: resolvedTimeZone } : {}),
  }).format(parsed);
};

export const getBrowserTimeZone = (): string | null => {
  try {
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return typeof timeZone === "string" && timeZone.trim().length > 0 ? timeZone : null;
  } catch {
    return null;
  }
};

export const normalizeTimeZone = (
  timeZone?: string | null,
  fallback?: string | null,
): string | null => {
  const candidates = [timeZone, fallback];

  for (const candidate of candidates) {
    const normalized = candidate?.trim();
    if (!normalized) {
      continue;
    }

    try {
      new Intl.DateTimeFormat(undefined, { timeZone: normalized }).format(new Date(0));
      return normalized;
    } catch {
      // Try the fallback candidate when provided.
    }
  }

  return null;
};

export const resolveGigTimeZone = (timeZone?: string | null): string => {
  return normalizeTimeZone(timeZone, DEFAULT_GIG_TIME_ZONE) ?? DEFAULT_GIG_TIME_ZONE;
};

const twoDigit = (value: number) => String(value).padStart(2, "0");

const parseDateInputParts = (value: string): ZonedDateParts | null => {
  if (!ISO_DATE_REGEX.test(value)) {
    return null;
  }

  const [yearPart, monthPart, dayPart] = value.split("-");
  const year = Number.parseInt(yearPart, 10);
  const month = Number.parseInt(monthPart, 10);
  const day = Number.parseInt(dayPart, 10);
  const parsed = new Date(Date.UTC(year, month - 1, day));

  if (
    !Number.isInteger(year) ||
    !Number.isInteger(month) ||
    !Number.isInteger(day) ||
    parsed.getUTCFullYear() !== year ||
    parsed.getUTCMonth() + 1 !== month ||
    parsed.getUTCDate() !== day
  ) {
    return null;
  }

  return { year, month, day };
};

const parseDateTimeLocalParts = (value: string): ZonedDateTimeParts | null => {
  const match = DATE_TIME_LOCAL_REGEX.exec(value);
  if (!match) {
    return null;
  }

  const [, yearPart, monthPart, dayPart, hourPart, minutePart] = match;
  const dateParts = parseDateInputParts(`${yearPart}-${monthPart}-${dayPart}`);
  if (!dateParts) {
    return null;
  }

  const hour = Number.parseInt(hourPart, 10);
  const minute = Number.parseInt(minutePart, 10);
  if (hour < 0 || hour > 23 || minute < 0 || minute > 59) {
    return null;
  }

  return {
    ...dateParts,
    hour,
    minute,
    second: 0,
    millisecond: 0,
  };
};

export const getDateTimePartsInTimeZone = (
  date: Date,
  timeZone?: string | null,
): ZonedDateTimeParts | null => {
  const resolvedTimeZone = normalizeTimeZone(timeZone);
  if (!resolvedTimeZone || Number.isNaN(date.getTime())) {
    return null;
  }

  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone: resolvedTimeZone,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
  const values = Object.fromEntries(formatter.formatToParts(date).map((part) => [part.type, part.value]));

  return {
    year: Number(values.year),
    month: Number(values.month),
    day: Number(values.day),
    hour: Number(values.hour),
    minute: Number(values.minute),
    second: Number(values.second),
    millisecond: date.getMilliseconds(),
  };
};

export const toZonedIsoDate = (date: Date, timeZone?: string | null): string => {
  const parts = getDateTimePartsInTimeZone(date, resolveGigTimeZone(timeZone));
  if (!parts) {
    return toLocalIsoDate(date);
  }

  return `${parts.year}-${twoDigit(parts.month)}-${twoDigit(parts.day)}`;
};

const getTimeZoneOffsetMs = (date: Date, timeZone: string): number => {
  const parts = getDateTimePartsInTimeZone(date, timeZone);
  if (!parts) {
    return 0;
  }

  const localAsUtc = Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour,
    parts.minute,
    parts.second,
    parts.millisecond,
  );
  return localAsUtc - date.getTime();
};

export const zonedDateTimePartsToDate = (
  parts: ZonedDateTimeParts,
  timeZone?: string | null,
): Date | null => {
  const resolvedTimeZone = resolveGigTimeZone(timeZone);
  const utcGuess = Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour,
    parts.minute,
    parts.second,
    parts.millisecond,
  );
  const guessedOffset = getTimeZoneOffsetMs(new Date(utcGuess), resolvedTimeZone);
  let timestamp = utcGuess - guessedOffset;
  const adjustedOffset = getTimeZoneOffsetMs(new Date(timestamp), resolvedTimeZone);

  if (adjustedOffset !== guessedOffset) {
    timestamp = utcGuess - adjustedOffset;
  }

  const parsed = new Date(timestamp);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }

  const resolvedParts = getDateTimePartsInTimeZone(parsed, resolvedTimeZone);
  if (
    !resolvedParts ||
    resolvedParts.year !== parts.year ||
    resolvedParts.month !== parts.month ||
    resolvedParts.day !== parts.day ||
    resolvedParts.hour !== parts.hour ||
    resolvedParts.minute !== parts.minute ||
    resolvedParts.second !== parts.second ||
    resolvedParts.millisecond !== parts.millisecond
  ) {
    return null;
  }

  return parsed;
};

export const zonedStartOfDay = (value: string, timeZone?: string | null): Date | null => {
  const parts = parseDateInputParts(value);
  if (!parts) {
    return null;
  }

  return zonedDateTimePartsToDate({
    ...parts,
    hour: 0,
    minute: 0,
    second: 0,
    millisecond: 0,
  }, timeZone);
};

export const zonedEndOfDay = (value: string, timeZone?: string | null): Date | null => {
  const parts = parseDateInputParts(value);
  if (!parts) {
    return null;
  }

  return zonedDateTimePartsToDate({
    ...parts,
    hour: 23,
    minute: 59,
    second: 59,
    millisecond: 999,
  }, timeZone);
};

// Build a UTC ISO date string (YYYY-MM-DD) for input min/max attributes.
// Example: toIsoDate(new Date(Date.UTC(2026, 2, 8))) => "2026-03-08"
export const toIsoDate = (date: Date) => {
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, "0");
  const day = String(date.getUTCDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

// Helper for creating date boundaries relative to "today".
// Example: subtractYears(new Date("2026-03-08T00:00:00Z"), 2) => Date for 2024-03-08
export const subtractYears = (date: Date, years: number) => {
  const next = new Date(date.getTime());
  next.setUTCFullYear(next.getUTCFullYear() - years);
  return next;
};

// Parse and validate an ISO date without timezone drift.
// Example: parseIsoDate("2008-05-14") => Date for 2008-05-14T00:00:00.000Z
export const parseIsoDate = (value: string): Date | null => {
  if (!ISO_DATE_REGEX.test(value)) {
    return null;
  }

  const [yearPart, monthPart, dayPart] = value.split("-");
  const year = Number.parseInt(yearPart, 10);
  const month = Number.parseInt(monthPart, 10);
  const day = Number.parseInt(dayPart, 10);

  if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day)) {
    return null;
  }

  const parsed = new Date(Date.UTC(year, month - 1, day));
  if (
    parsed.getUTCFullYear() !== year ||
    parsed.getUTCMonth() + 1 !== month ||
    parsed.getUTCDate() !== day
  ) {
    return null;
  }

  return parsed;
};

// Example: formatDate("2026-03-08") => localized date string (e.g. "Mar 8, 2026")
export const formatDate = (value: string | null, fallback = "Not set"): string => {
  return formatDateValue(value, {
    month: "short",
    day: "numeric",
    year: "numeric",
  }, fallback);
};

export const formatDateInGigTimeZone = (
  value: string | null,
  timeZone?: string | null,
  fallback = "Not set",
): string => {
  return formatDateValue(value, {
    month: "short",
    day: "numeric",
    year: "numeric",
  }, fallback, resolveGigTimeZone(timeZone));
};

// Example: formatDateTime("2026-03-08T14:30:00Z") => localized datetime string (e.g. "3/8/2026, 10:30 AM")
export const formatDateTime = (value: string | null, fallback = "Not set"): string => {
  return formatDateValue(value, {
    month: "numeric",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }, fallback);
};

export const formatDateTimeInGigTimeZone = (
  value: string | null,
  timeZone?: string | null,
  fallback = "Not set",
): string => {
  return formatDateValue(value, {
    month: "numeric",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short",
  }, fallback, resolveGigTimeZone(timeZone));
};

// Example: formatShortDateTime("2026-03-08T14:30:00Z") => localized short datetime string (e.g. "Mar 8, 10:30 AM")
export const formatShortDateTime = (value: string | null, fallback = "Not set"): string => {
  return formatDateValue(value, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }, fallback);
};

export const formatShortDateTimeInGigTimeZone = (
  value: string | null,
  timeZone?: string | null,
  fallback = "Not set",
): string => {
  return formatDateValue(value, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short",
  }, fallback, resolveGigTimeZone(timeZone));
};

// Example: formatLongDate("2026-03-08T14:30:00Z") => localized long date string (e.g. "Sunday, March 8, 2026")
export const formatLongDate = (value: string | null, fallback = "Not set"): string => {
  return formatDateValue(value, {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  }, fallback);
};

export const formatLongDateInTimeZone = (
  value: string | null,
  timeZone?: string | null,
  fallback = "Not set",
): string => {
  return formatDateValue(value, {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  }, fallback, resolveGigTimeZone(timeZone));
};

export const formatWeekdayInTimeZone = (
  value: string | null,
  timeZone?: string | null,
): string | null => {
  if (!value) {
    return null;
  }

  const parsed = parseDateValue(value);
  if (!parsed) {
    return null;
  }

  return new Intl.DateTimeFormat(undefined, {
    timeZone: resolveGigTimeZone(timeZone),
    weekday: "long",
  }).format(parsed);
};

// Example: formatMonthYear("2026-03-08T14:30:00Z") => localized month-year string (e.g. "Mar 2026")
export const formatMonthYear = (value: string | null, fallback = "Not set"): string => {
  return formatDateValue(value, {
    month: "short",
    year: "numeric",
  }, fallback);
};

// Example: formatDateTimeInTimeZone("2026-03-08T14:30:00Z", "America/New_York") => "Sunday, Mar 8 at 10:30 AM EDT"
export const formatDateTimeInTimeZone = (
  value: string | null,
  timeZone: string | null | undefined,
  fallback: string | null = "Not set",
): string | null => {
  if (!value) {
    return fallback;
  }

  const parsed = parseDateValue(value);
  if (!parsed) {
    return fallback === null ? null : value;
  }

  const resolvedTimeZone = resolveGigTimeZone(timeZone);

  const dateLabel = new Intl.DateTimeFormat(undefined, {
    timeZone: resolvedTimeZone,
    weekday: "long",
    month: "short",
    day: "numeric",
  }).format(parsed);

  const timeLabel = new Intl.DateTimeFormat(undefined, {
    timeZone: resolvedTimeZone,
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short",
  }).format(parsed);

  return `${dateLabel} at ${timeLabel}`;
};

// Build a local ISO date string (YYYY-MM-DD) for date inputs.
// Example: toLocalIsoDate(new Date(2026, 2, 8)) => "2026-03-08"
export const toLocalIsoDate = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

// Parse YYYY-MM-DD into a local Date at midnight.
// Example: parseLocalDateInput("2026-03-08") => local Date for 2026-03-08 00:00:00
export const parseLocalDateInput = (value: string): Date | null => {
  if (!value) {
    return null;
  }

  const [year, month, day] = value.split("-").map(Number);
  if (!year || !month || !day) {
    return null;
  }

  return new Date(year, month - 1, day);
};

// Example: addDays(new Date("2026-03-08T00:00:00Z"), 7) => Date for 2026-03-15
export const addDays = (date: Date, days: number): Date => {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
};

// Example: startOfLocalDay(new Date("2026-03-08T15:45:00")) => local Date at 00:00:00.000
export const startOfLocalDay = (date: Date): Date => {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);
};

// Example: endOfLocalDay(new Date("2026-03-08T15:45:00")) => local Date at 23:59:59.999
export const endOfLocalDay = (date: Date): Date => {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);
};

// Example: toIsoTimestamp(new Date("2026-03-08T15:45:00Z")) => "2026-03-08T15:45:00.000Z"
export const toIsoTimestamp = (date: Date): string => {
  return date.toISOString();
};

// Convert a datetime-local input value into ISO, or null when empty/invalid.
// Example: toOffsetIsoString("2026-03-08T10:30") => ISO timestamp string like "2026-03-08T15:30:00.000Z"
export const toOffsetIsoString = (value: string): string | null => {
  if (!value) {
    return null;
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return date.toISOString();
};

export const toZonedIsoString = (value: string, timeZone?: string | null): string | null => {
  if (!value) {
    return null;
  }

  const parts = parseDateTimeLocalParts(value);
  if (!parts) {
    return null;
  }

  return zonedDateTimePartsToDate(parts, timeZone)?.toISOString() ?? null;
};

// Convert an ISO timestamp into a datetime-local input value.
// Example: toDateTimeLocalInput("2026-03-08T15:30:00.000Z") => "2026-03-08T10:30" (local-time dependent)
export const toDateTimeLocalInput = (value: string | null): string => {
  if (!value) {
    return "";
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return "";
  }

  const year = parsed.getFullYear();
  const month = String(parsed.getMonth() + 1).padStart(2, "0");
  const day = String(parsed.getDate()).padStart(2, "0");
  const hour = String(parsed.getHours()).padStart(2, "0");
  const minute = String(parsed.getMinutes()).padStart(2, "0");
  return `${year}-${month}-${day}T${hour}:${minute}`;
};

export const toDateTimeLocalInputInTimeZone = (
  value: string | null,
  timeZone?: string | null,
): string => {
  if (!value) {
    return "";
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return "";
  }

  const parts = getDateTimePartsInTimeZone(parsed, resolveGigTimeZone(timeZone));
  if (!parts) {
    return "";
  }

  return `${parts.year}-${twoDigit(parts.month)}-${twoDigit(parts.day)}T${twoDigit(parts.hour)}:${twoDigit(parts.minute)}`;
};

// Normalize a Date to minute precision by clearing seconds/milliseconds.
// Example: toMinutePrecision(new Date("2026-03-08T10:30:45.120Z")) => Date for ...10:30:00.000Z
export const toMinutePrecision = (value: Date): Date => {
  const next = new Date(value.getTime());
  next.setSeconds(0, 0);
  return next;
};

// Example: formatEstimatedDuration(90) => "1h 30m"
/**
 * Coarse "how long ago" label. Used where an exact timestamp would be more precise than the reader
 * needs, such as the publicly visible recently completed gigs panel.
 */
export const formatRelativeAge = (value: string | null, fallback = "Recently"): string => {
  const parsed = value ? new Date(value) : null;
  if (!parsed || Number.isNaN(parsed.getTime())) {
    return fallback;
  }

  const elapsedMs = Date.now() - parsed.getTime();
  if (elapsedMs < 0) {
    return "Just now";
  }

  const elapsedMinutes = Math.floor(elapsedMs / 60000);
  if (elapsedMinutes < 60) {
    return "Just now";
  }

  const elapsedHours = Math.floor(elapsedMinutes / 60);
  if (elapsedHours < 24) {
    return elapsedHours === 1 ? "1 hour ago" : `${elapsedHours} hours ago`;
  }

  const elapsedDays = Math.floor(elapsedHours / 24);
  if (elapsedDays < 7) {
    return elapsedDays === 1 ? "Yesterday" : `${elapsedDays} days ago`;
  }

  const elapsedWeeks = Math.floor(elapsedDays / 7);
  if (elapsedDays < 30) {
    return elapsedWeeks === 1 ? "1 week ago" : `${elapsedWeeks} weeks ago`;
  }

  const elapsedMonths = Math.floor(elapsedDays / 30);
  if (elapsedMonths < 12) {
    return elapsedMonths === 1 ? "1 month ago" : `${elapsedMonths} months ago`;
  }

  const elapsedYears = Math.floor(elapsedDays / 365);
  return elapsedYears <= 1 ? "1 year ago" : `${elapsedYears} years ago`;
};

export const formatEstimatedDuration = (minutes: number | null): string => {
  if (minutes === null || minutes <= 0) {
    return "Not set";
  }

  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  if (hours > 0 && remainingMinutes > 0) {
    return `${hours}h ${remainingMinutes}m`;
  }
  if (hours > 0) {
    return `${hours}h`;
  }

  return `${remainingMinutes}m`;
};
